/**
 * Health API client for code-factory.
 * The /api/health/live and /api/health/info endpoints are public — no auth required.
 */

const BASE =
  (import.meta.env.VITE_ARAF_FACTORY_URL as string | undefined) ?? 'http://localhost:3550';

export interface ServiceStatus {
  status: 'up' | 'down';
  url?: string;
  message?: string;
}

export interface HealthStatus {
  status: 'ok' | 'error' | 'shutting_down';
  /** Services that are healthy */
  info: Record<string, ServiceStatus>;
  /** Services that have errors */
  error: Record<string, ServiceStatus>;
  details: Record<string, ServiceStatus>;
}

export interface HealthMemory {
  rss: number;
  heapUsed: number;
  heapTotal: number;
  external: number;
}

export interface HealthInfo {
  version: string;
  commit: string;
  uptime: number;
  memory: HealthMemory;
  node: string;
  environment: string;
}

/**
 * Fetch the health check result. Returns the body on both 200 (ok) and 503 (degraded).
 * Returns null on unexpected errors or non-health responses.
 */
export async function fetchHealth(): Promise<HealthStatus | null> {
  const url = `${BASE}/api/health/live`;
  try {
    console.debug('[healthApi] GET', url);
    const res = await fetch(url);
    console.debug('[healthApi] status', res.status, url);
    if (res.status === 200 || res.status === 503) {
      return res.json() as Promise<HealthStatus>;
    }
    console.warn('[healthApi] unexpected status', res.status, url);
    return null;
  } catch (err) {
    console.error('[healthApi] fetch error', url, err);
    return null;
  }
}

/**
 * Fetch version and system info from the backend.
 */
export async function fetchHealthInfo(): Promise<HealthInfo | null> {
  const url = `${BASE}/api/health/info`;
  try {
    console.debug('[healthApi] GET', url);
    const res = await fetch(url);
    console.debug('[healthApi] status', res.status, url);
    if (!res.ok) {
      console.warn('[healthApi] non-ok status', res.status, url);
      return null;
    }
    return res.json() as Promise<HealthInfo>;
  } catch (err) {
    console.error('[healthApi] fetch error', url, err);
    return null;
  }
}

/** Merge info + error maps into a flat service map. */
export function getAllServices(health: HealthStatus): Record<string, ServiceStatus> {
  return { ...health.info, ...health.error };
}
